import { Injectable } from '@angular/core';
import { MachineApiService } from './machine-api.service';

type ApiResponse<T> = { data: T; success: boolean };

export type WhatsappIntegrationStatus = {
  projectId: string;
  hasProjectPhoneMapping: boolean;
  projectPhoneNumberId?: string;
  linkedPhoneNumber: string | null;
  linkedAt: number | null;
  verifiedAt: number | null;
  isLinked: boolean;
};

export type WhatsappProjectMessage = {
  id: string;
  projectId: string;
  organizationId?: string;
  userId?: string;
  provider: string;
  projectPhoneNumberId: string;
  providerMessageId: string;
  phoneNumber: string;
  waId?: string;
  direction: 'inbound' | 'outbound';
  messageType: string;
  text?: string;
  status: string;
  timestamp: number;
  meta?: any;
  raw?: any;
};

@Injectable({ providedIn: 'root' })
export class WhatsappIntegrationService {
  constructor(private api: MachineApiService) {}

  private async unwrap<T>(p: Promise<ApiResponse<T>>): Promise<T> {
    const res = await p;
    return res.data;
  }

  async integrationStatus(projectId: string): Promise<WhatsappIntegrationStatus> {
    const q = new URLSearchParams({ projectId }).toString();
    return this.unwrap(
      this.api.request<ApiResponse<WhatsappIntegrationStatus>>(`/api/whatsapp/integration/status?${q}`)
    );
  }

  async requestOtp(input: {
    projectId: string;
    organizationId?: string;
    phoneNumber: string;
  }): Promise<{ expiresAt: number }> {
    return this.unwrap(
      this.api.postJson<ApiResponse<{ expiresAt: number }>>('/api/phone-link/whatsapp/request-otp', input)
    );
  }

  async verifyOtp(input: {
    projectId: string;
    organizationId?: string;
    phoneNumber: string;
    code: string;
  }): Promise<{ linked: boolean }> {
    return this.unwrap(
      this.api.postJson<ApiResponse<{ linked: boolean }>>('/api/phone-link/whatsapp/verify-otp', input)
    );
  }

  async unlink(projectId: string): Promise<{ unlinked: boolean }> {
    return this.unwrap(
      this.api.postJson<ApiResponse<{ unlinked: boolean }>>('/api/phone-link/whatsapp/unlink', { projectId })
    );
  }

  async listMessages(projectId: string, limit = 100): Promise<WhatsappProjectMessage[]> {
    const q = new URLSearchParams({ projectId, limit: String(limit) }).toString();
    const res = await this.api.request<ApiResponse<{ data: WhatsappProjectMessage[] }>>(`/api/whatsapp/messages?${q}`);
    return (res.data?.data ?? []) as WhatsappProjectMessage[];
  }
}
